import { SAH_READINESS_CHECK_CODES } from '@statura/utils/sah-delivery-ladder';
import type { RecommendationKind } from '@/lib/supabase/queries';
import type { CarePlanComposerSectionKey } from '@/lib/care-plans/journey';
import {
  resolveAcknowledgementBlockerForStatus,
  CARE_PLAN_SUBMIT_FOR_REVIEW_ANCHOR,
} from '@/lib/care-plans/care-plan-acknowledgement-resolution';
import {
  carePlanBlockerResolution,
  isCarePlanAcknowledgementBlocker,
  isCarePlanShapeSection,
  PLAN_SHAPE_ANCHOR,
  type CarePlanBlockerResolution,
} from '@/lib/care-plans/care-plan-blocker-resolution';

export { PLAN_SHAPE_ANCHOR };
import {
  carePlanDetailHref,
  type ClientCarePlanReturnContext,
} from '@/lib/sah/client-care-plan-navigation';

/** Composer page anchors — section ids, workflow blocks, or readiness targets. */
export type ComposerPageAnchor =
  | CarePlanComposerSectionKey
  | 'composer-assess'
  | 'composer-agree'
  | 'composer-activate'
  | typeof CARE_PLAN_SUBMIT_FOR_REVIEW_ANCHOR
  | 'import-inbox'
  | typeof PLAN_SHAPE_ANCHOR;

export type ComposerAnchorTarget =
  | { kind: 'section'; section: CarePlanComposerSectionKey }
  | { kind: 'element'; elementId: ComposerPageAnchor }
  | { kind: 'block'; blockKey: string; elementId?: ComposerPageAnchor };

const IMPORT_DESTINATION_SECTION: Partial<
  Record<RecommendationKind | string, CarePlanComposerSectionKey>
> = {
  CARE_PLAN_GOAL: 'goals',
  CARE_PLAN_RISK: 'risks',
  CARE_PLAN_INTERVENTION: 'interventions',
  CARE_PLAN_PREFERENCE: 'preferences',
  CARE_PLAN_WORKER_INSTRUCTION: 'worker_instructions',
  CARE_PLAN_SERVICE_INCLUSION: 'services',
  SAH_BUDGET_REVIEW: 'budget',
  SAH_SUPPORT_PLAN_REVIEW_REQUEST: 'reviews',
  SAH_CLASSIFICATION_REVIEW: 'reviews',
  // NEXT_ASSESSMENT is deliberately unmapped — accepting a follow-on creates
  // an assessment + workflow task, not composer content, so there is no
  // section to focus (the inbox accept path checks this and skips focus).
};

const ACTIVATION_SECTION_ANCHOR: Record<string, ComposerPageAnchor> = {
  goals: 'goals',
  risks: 'risks',
  interventions: 'interventions',
  preferences: 'preferences',
  worker_instructions: 'worker_instructions',
  services: 'services',
  budget: 'budget',
  reviews: 'reviews',
  acknowledgement: 'composer-agree',
  restorative_episode: PLAN_SHAPE_ANCHOR,
  advance_care_directive: PLAN_SHAPE_ANCHOR,
  after_hours_escalation: PLAN_SHAPE_ANCHOR,
  rac_respite_transition: PLAN_SHAPE_ANCHOR,
};

const ACTIVATION_CHECK_ANCHOR: Record<string, ComposerPageAnchor> = {
  assessment_imports_reviewed: 'import-inbox',
  assessment_sources_recent_or_overridden: 'import-inbox',
  assessment_sources_not_superseded: 'import-inbox',
};

const BLOCKER_ANCHOR_BLOCK: Partial<Record<string, string>> = {
  agreements: 'agreement_evidence',
  'composer-agree': 'agreement_evidence',
  'import-inbox': 'care_team_inbox',
  'composer-assess': 'conversation_capture',
};

export function importDestinationComposerSection(
  destination: RecommendationKind | string,
): CarePlanComposerSectionKey | null {
  const key = String(destination ?? '')
    .trim()
    .toUpperCase();
  return IMPORT_DESTINATION_SECTION[key] ?? null;
}

export function resolveComposerAnchorTarget(input: {
  anchor: ComposerPageAnchor;
}): ComposerAnchorTarget {
  const anchor = input.anchor;
  const section = importDestinationComposerSection(anchor);
  if (section) return { kind: 'section', section };

  const knownSections = new Set<CarePlanComposerSectionKey>([
    'goals',
    'risks',
    'interventions',
    'preferences',
    'worker_instructions',
    'services',
    'budget',
    'reviews',
    'support_profile',
    'medical_status_flags',
  ]);
  if (knownSections.has(anchor as CarePlanComposerSectionKey)) {
    return { kind: 'section', section: anchor as CarePlanComposerSectionKey };
  }

  const blockKey = BLOCKER_ANCHOR_BLOCK[anchor];
  if (blockKey) {
    return { kind: 'block', blockKey, elementId: anchor };
  }

  return { kind: 'element', elementId: anchor };
}

export function activationCheckComposerAnchor(input: {
  section?: string | null;
  key?: string | null;
  planStatus?: string | null;
}): ComposerPageAnchor {
  if (input.section && isCarePlanShapeSection(input.section)) {
    return PLAN_SHAPE_ANCHOR;
  }
  const byKey = input.key ? ACTIVATION_CHECK_ANCHOR[input.key] : null;
  if (byKey) return byKey;
  if (input.section === 'acknowledgement' || input.key === 'acknowledgement') {
    const ack = resolveAcknowledgementBlockerForStatus(input.planStatus);
    return ack.hash;
  }
  const bySection = input.section ? ACTIVATION_SECTION_ANCHOR[input.section] : null;
  return bySection ?? 'composer-activate';
}

export function planShapeBlockerHref(
  carePlanId: string,
  returnContext?: ClientCarePlanReturnContext,
): string {
  return returnContext
    ? carePlanDetailHref(carePlanId, { hash: PLAN_SHAPE_ANCHOR, returnContext })
    : `/care-plans/${carePlanId}#${PLAN_SHAPE_ANCHOR}`;
}

export type ComposerBlockerComposerResolution = Omit<CarePlanBlockerResolution, 'label'> & {
  href: string;
  label: string | null;
};

export function composerBlockerComposerResolution(
  blocker: string,
  planStatus?: string | null,
  options?: {
    carePlanId?: string;
    returnContext?: ClientCarePlanReturnContext;
  },
): ComposerBlockerComposerResolution {
  const resolution = carePlanBlockerResolution(blocker);
  const status = String(planStatus ?? '').toUpperCase();
  const lower = blocker.trim().toLowerCase();

  if (resolution.hash === PLAN_SHAPE_ANCHOR && options?.carePlanId) {
    return {
      ...resolution,
      href: planShapeBlockerHref(options.carePlanId, options.returnContext),
    };
  }

  if (resolution.hash === 'composer-agree' && isCarePlanAcknowledgementBlocker(blocker)) {
    const ack = resolveAcknowledgementBlockerForStatus(planStatus);
    return {
      ...resolution,
      label: ack.label,
      hash: ack.hash,
      actionLabel: ack.actionLabel,
      href: `#${ack.hash}`,
    };
  }

  if (lower.includes('care plan status does not allow approval request')) {
    if (status === 'ACTIVE') {
      return {
        label: null,
        hash: resolution.hash,
        actionLabel: resolution.actionLabel,
        href: '#composer-activate',
      };
    }
    return {
      label:
        'Complete readiness gaps below, then use the terminal action panel — this plan status cannot request approval yet.',
      hash: resolution.hash,
      actionLabel: resolution.actionLabel,
      href: '#composer-activate',
    };
  }

  const hash = resolution.hash ?? 'composer-activate';
  return {
    ...resolution,
    href: `#${hash}`,
  };
}

export function composerHashHref(hash: ComposerPageAnchor): string {
  return `#${hash}`;
}

/** SSOT: draft compose-surface anchor for each SAH delivery-readiness check code. */
export const DELIVERY_CHECK_COMPOSE_ANCHOR: Partial<Record<string, ComposerPageAnchor>> = {
  ACTIVE_SUPPORT_AT_HOME_PLAN: 'composer-activate',
  ACKNOWLEDGED_CARE_PLAN: 'composer-agree',
  DATE_VALID_SERVICE_INCLUSIONS: 'services',
  SIGNED_SERVICE_AGREEMENT: 'composer-agree',
  // GENERAL_CARE_CONSENT is deliberately NOT mapped to a compose anchor — the
  // agreement block only captures SAH-specific consent types, so it must route
  // to the client Consent Register where general care consent is recorded
  // (adversarial audit P1). deliveryRoute() special-cases it.
  CURRENT_QUARTER_BUDGET: 'budget',
  SAH_MEANS_BASIS: 'budget',
  HCP_OPENING_RECORDED: 'budget',
};

const DELIVERY_CODE_SECTION: Partial<Record<string, CarePlanComposerSectionKey>> = {
  DATE_VALID_SERVICE_INCLUSIONS: 'services',
  CURRENT_QUARTER_BUDGET: 'budget',
  SAH_MEANS_BASIS: 'budget',
  HCP_OPENING_RECORDED: 'budget',
};

const DELIVERY_BLOCKER_PATTERNS: Partial<Record<string, RegExp>> = {
  DATE_VALID_SERVICE_INCLUSIONS: /service inclusion|typed.*service/i,
  CURRENT_QUARTER_BUDGET: /quarter.*budget|budget not loaded|budget.*lane/i,
  SAH_MEANS_BASIS: /contribution basis|means/i,
  ACKNOWLEDGED_CARE_PLAN: /acknowledg/i,
  SIGNED_SERVICE_AGREEMENT: /service agreement|agreement ledger/i,
  GENERAL_CARE_CONSENT: /general care consent|care consent/i,
  ACTIVE_SUPPORT_AT_HOME_PLAN: /activation readiness|activate this plan/i,
  HCP_OPENING_RECORDED: /hcp.*opening|opening recorded/i,
};

export function deliveryCheckComposerAnchor(
  code: string,
  planStatus?: string | null,
): ComposerPageAnchor | null {
  if (String(code) === 'ACKNOWLEDGED_CARE_PLAN') {
    return resolveAcknowledgementBlockerForStatus(planStatus).hash;
  }
  return DELIVERY_CHECK_COMPOSE_ANCHOR[String(code)] ?? null;
}

export function composerBlockerDeliveryCode(blocker: string): string | null {
  const raw = blocker.trim();
  for (const [code, pattern] of Object.entries(DELIVERY_BLOCKER_PATTERNS)) {
    if (pattern?.test(raw)) return code;
  }
  return null;
}

export function composerBlockerCanonicalLabel(blocker: string, planStatus?: string | null): string {
  const resolution = carePlanBlockerResolution(blocker);
  if (isCarePlanAcknowledgementBlocker(blocker)) {
    const ack = resolveAcknowledgementBlockerForStatus(planStatus);
    if (ack.label) return ack.label;
  }
  const deliveryCode = composerBlockerDeliveryCode(blocker);
  if (deliveryCode) {
    const metadata = SAH_READINESS_CHECK_CODES.find((entry) => entry.code === deliveryCode);
    if (metadata) return metadata.label;
  }
  return resolution.label;
}

export function readinessBlockerMatchesDeliveryCode(
  readinessBlockers: readonly string[],
  code: string,
  planStatus?: string | null,
): boolean {
  const deliveryAnchor = deliveryCheckComposerAnchor(code, planStatus);
  if (!deliveryAnchor) return false;

  const pattern = DELIVERY_BLOCKER_PATTERNS[String(code)];
  return readinessBlockers.some((blocker) => {
    if (String(code) === 'ACKNOWLEDGED_CARE_PLAN' && isCarePlanAcknowledgementBlocker(blocker)) {
      return true;
    }
    const resolution = composerBlockerComposerResolution(blocker, planStatus);
    const blockerAnchor = resolution.href.replace(/^#/, '');
    if (blockerAnchor === deliveryAnchor) return true;
    return pattern ? pattern.test(blocker) : false;
  });
}

export function deliveryCheckDuplicatedByOpenActivationChecks<
  T extends { key: string; section?: string | null; satisfied: boolean },
>(code: string, activationChecks: readonly T[], planStatus?: string | null): boolean {
  const deliveryAnchor = deliveryCheckComposerAnchor(code, planStatus);
  if (!deliveryAnchor) return false;
  return activationChecks.some((check) => {
    if (check.satisfied) return false;
    const activationAnchor = activationCheckComposerAnchor({
      section: check.section,
      key: check.key,
      planStatus,
    });
    return activationAnchor === deliveryAnchor;
  });
}

export function filterComposerDeliveryChecks<T extends { code: string; satisfied: boolean }>(
  checks: readonly T[],
  readinessBlockers: readonly string[],
  planStatus?: string | null,
  activationChecks?: readonly {
    key: string;
    section?: string | null;
    satisfied: boolean;
  }[],
): T[] {
  return checks.filter((check) => {
    if (check.satisfied) return true;
    if (
      readinessBlockers.length > 0 &&
      readinessBlockerMatchesDeliveryCode(readinessBlockers, check.code, planStatus)
    ) {
      return false;
    }
    if (
      activationChecks &&
      activationChecks.length > 0 &&
      deliveryCheckDuplicatedByOpenActivationChecks(check.code, activationChecks, planStatus)
    ) {
      return false;
    }
    return true;
  });
}

export function activationCheckDuplicatedByReadinessBlockers(
  check: { key?: string | null; section?: string | null; label: string },
  readinessBlockers: readonly string[],
  planStatus?: string | null,
): boolean {
  if (readinessBlockers.length === 0) return false;
  const checkAnchor = activationCheckComposerAnchor({
    section: check.section,
    key: check.key,
    planStatus,
  });
  const acknowledgementCheck =
    check.section === 'acknowledgement' || check.key === 'acknowledgement';
  return readinessBlockers.some((blocker) => {
    if (acknowledgementCheck && isCarePlanAcknowledgementBlocker(blocker)) return true;
    const resolution = composerBlockerComposerResolution(blocker, planStatus);
    const blockerAnchor = resolution.href.replace(/^#/, '');
    if (blockerAnchor === checkAnchor) return true;
    return false;
  });
}

export function filterComposerActivationChecks<
  T extends { key: string; label: string; section?: string | null; satisfied: boolean },
>(checks: readonly T[], readinessBlockers: readonly string[], planStatus?: string | null): T[] {
  if (readinessBlockers.length === 0) return [...checks];
  return checks.filter(
    (check) =>
      check.satisfied ||
      !activationCheckDuplicatedByReadinessBlockers(check, readinessBlockers, planStatus),
  );
}

export function sectionHasOpenComposerBlockers(
  sectionKey: CarePlanComposerSectionKey,
  input: {
    readinessBlockers: string[];
    deliveryChecks?: readonly { code: string; satisfied: boolean }[];
  },
): boolean {
  for (const blocker of input.readinessBlockers) {
    const resolution = composerBlockerComposerResolution(blocker);
    if (resolution.href === `#${sectionKey}`) return true;
  }
  for (const check of input.deliveryChecks ?? []) {
    if (check.satisfied) continue;
    if (DELIVERY_CODE_SECTION[check.code] === sectionKey) return true;
  }
  return false;
}

/** Delivery checks that never resolve on the compose surface (route elsewhere). */
export const OFF_COMPOSE_DELIVERY_CHECK_CODES = ['GENERAL_CARE_CONSENT'] as const;

export function deliveryCheckResolvesOnComposeSurface(
  code: string,
  planStatus?: string | null,
): boolean {
  if ((OFF_COMPOSE_DELIVERY_CHECK_CODES as readonly string[]).includes(code)) return false;
  return deliveryCheckComposerAnchor(code, planStatus) !== null;
}

export function offComposeDeliveryResolutionHint(code: string): string | null {
  switch (code) {
    case 'GENERAL_CARE_CONSENT':
      return 'General care consent is recorded in the client Consent Register';
    default:
      return null;
  }
}

export function composerReadinessDedupeNotice(
  openOffComposeDeliveryCodes: readonly string[],
): string {
  const hints = openOffComposeDeliveryCodes
    .map(offComposeDeliveryResolutionHint)
    .filter((hint): hint is string => Boolean(hint));
  const base =
    'Plan activation blockers above are the single resolve path for overlapping activation and delivery-ready gaps on this page';
  if (hints.length === 0) {
    return (
      base +
      '. The activation and delivery-ready checklists below show only the remaining, non-overlapping work.'
    );
  }
  return (
    base +
    '. ' +
    hints.join('; ') +
    '. The checklists below show only the remaining, non-overlapping work.'
  );
}
